import React from 'react';
import PropTypes from 'prop-types';
import Checkbox from 'react-toolbox/lib/checkbox';
import FontIcon from 'react-toolbox/lib/font_icon';
import { CardText } from 'react-toolbox/lib/card';
import Form, {
  formatPhoneNumber,
  scrubNumber,
} from '../../../../../common/components/Form';
import ThemedInput from '../../../../../common/components/ThemedInput';
import { normalizePropertyPath } from '../../../../../common/utils/property-path';
import { fillWithDefaults } from '../../../utils/defaultDataHelper';
import { buildAddress } from '../index.js';
import styles from '../MembershipInfo.css';

const businessAddressIcons = {
  Business: 'business',
  Mailing: 'markunread_mailbox',
};

const buildOrgWithBusinessNumber = organization => {
  const phoneNumbers = organization ? organization.phoneNumbers || [] : [];
  const businessNumber = {
    number:
      (phoneNumbers.find(phone => phone.type.toLowerCase() === 'business') || {}
      ).number || '',
    type: 'Business',
  };
  return {
    ...organization,
    businessNumber: {
      ...businessNumber,
      number: formatPhoneNumber('$1-$2-$3', businessNumber.number),
    },
  };
};

export default class BusinessEdit extends React.Component {
  static contextTypes = {
    activateServiceDialog: PropTypes.func,
  };
  static propTypes = {
    currentOrganization: PropTypes.shape({
      id: PropTypes.number,
      isAccountActive: PropTypes.bool,
      key: PropTypes.string,
      addresses: PropTypes.arrayOf(
        PropTypes.shape({
          addressLine1: PropTypes.string,
          addressLine2: PropTypes.string,
          city: PropTypes.string,
          postalCode: PropTypes.string,
          stateCode: PropTypes.string,
          type: PropTypes.strint,
        })
      ),
      membershipDetails: PropTypes.shape({
        originalJoinDate: PropTypes.string,
        renewalDate: PropTypes.string,
        statusType: PropTypes.string,
        type: PropTypes.string,
      }),
      organizationDetails: PropTypes.shape({
        name: PropTypes.string,
        websiteUrl: PropTypes.string,
      }),
      phoneNumbers: PropTypes.arrayOf(
        PropTypes.shape({
          extension: PropTypes.string,
          number: PropTypes.string,
          type: PropTypes.string,
        })
      ),
    }),
    actions: PropTypes.shape({
      organization: PropTypes.objectOf(PropTypes.func),
    }),
  };
  state = {
    errors: {
      form: [],
    },
  };

  constructor(props) {
    super(props);

    this.state = {
      organization: this.loadOrgFromProps(props),
      addressSettings: {
        default: {
          sameAsBusiness: false,
        },
      },
      errors: {
        form: [],
      },
    };
  }

  componentWillReceiveProps(nextProps) {
    if (!this.props.currentOrganization && nextProps.currentOrganization) {
      this.setState(prevState => ({
        ...prevState,
        organization: this.loadOrgFromProps(nextProps),
      }));
    }
  }

  loadOrgFromProps = props => {
    const { currentOrganization } = props;
    let org = currentOrganization;

    const addressTypes = ['Business', 'Mailing'];

    if (org) {
      org = fillWithDefaults({
        containingObject: org,
        fillType: 'address',
        propName: 'addresses',
        typeList: addressTypes,
      });
    }

    return buildOrgWithBusinessNumber({
      ...org,
      addresses: org.addresses.filter(
        address => addressTypes.indexOf(address.type) >= 0
      ),
    });
  };

  handleCopyAddress = addressType => (value, form) => {
    const { organization } = form;
    this.setState(prevState => ({
      ...prevState,
      organization,
      addressSettings: {
        ...prevState.addressSettings,
        [addressType]: {
          sameAsBusiness: value,
        },
      },
    }));
    return value;
  };

  renderAddressHeader = type => () => {
    return (
      <h4 className={styles.formSubHeader} key={`header_${type}`}>
        <FontIcon
          value={businessAddressIcons[type] || ''}
          className={styles.headerIcon}
        />
        {type} Address
      </h4>
    );
  };

  // TODO: handle user with no mailing address
  buildAddressSections = addressArr => {
    return addressArr.reduce((acc, curr, index) => {
      const { type } = curr;
      const checkboxName = `copyBusinessInto${type}`;
      const sameAsBusiness =
        type === 'Business'
          ? {}
          : {
              Component: Checkbox,
              name: checkboxName,
              label: 'Same as Business Address',
              onChange: this.handleCopyAddress(type),
              className: styles.sameAsBusiness,
              type: 'checkbox',
            };
      const addressFields = buildAddress(
        curr.pathOverride || `organization.addresses[${index}]`
      ).map(addressField => {
        let required = false;
        if (
          type.toLowerCase() === 'business' &&
          (addressField.name.includes('stateCode') ||
            addressField.name.includes('postalCode'))
        ) {
          required = true;
        }
        if (
          type.toLowerCase() === 'mailing' &&
          !addressField.name.includes('addressLine2')
        ) {
          required = true;
        }
        return {
          ...addressField,
          className: styles.noIcon,
          disabled: checkboxName,
          required,
        };
      });
      return [
        ...acc,
        this.renderAddressHeader(type),
        sameAsBusiness,
        ...addressFields,
      ];
    }, []);
  };

  getFormInputs = addressFields => [
    {
      Component: ThemedInput,
      name: 'organization.organizationDetails.name',
      label: 'Business Name',
      type: 'text',
      icon: 'work',
      required: true,
      primary: true,
    },
    {
      Component: ThemedInput,
      name: 'organization.organizationDetails.websiteUrl',
      label: 'Website',
      type: 'text',
      icon: 'language',
      primary: true,
    },
    ...this.buildAddressSections(addressFields),
    {
      Component: ThemedInput,
      name: 'organization.businessNumber.number',
      label: 'Business Phone',
      type: 'tel',
      icon: 'phone',
      primary: true,
      onChange: value => formatPhoneNumber('$1-$2-$3', value),
    },
  ];

  getAddresses = addresses => {
    const { organization } = this.state;
    const { addresses: currentAddresses } = organization || {};
    return addresses || currentAddresses || [];
  };

  getSameAsBusinessList = () => {
    const { addressSettings } = this.state;
    return (
      Object.keys(addressSettings || {}).filter(
        key => addressSettings[key].sameAsBusiness
      ) || []
    );
  };

  handleSubmit = ({ form }, onCompleteHandler) => {
    // clear any previous success or errors
    // save changes to state
    const { organization } = form;
    this.setState(prevState => ({
      ...prevState,
      organization: organization,
      submitSuccess: false,
      errors: {
        form: [],
      },
    }));

    // trim any empty addresses (having a type doesn't count as a value)
    let sentAddresses = organization.addresses.filter(address =>
      Object.keys(address).some(
        key => (key === 'type' ? false : address[key] && address[key].length)
      )
    );

    // handle addresses marked same as business
    const sameAsBusinessList = this.getSameAsBusinessList();
    if (sameAsBusinessList.length) {
      sentAddresses = sentAddresses.map(address => {
        if (sameAsBusinessList.some(item => item === address.type)) {
          const businessAddress = sentAddresses.find(
            sentAddress => sentAddress.type.toLowerCase() === 'business'
          );
          // copy business address into it
          return {
            ...businessAddress,
            type: address.type,
          };
        }
        return address;
      });
    }

    // build phoneNumbers and filter out any empty ones
    const { businessNumber, ...rest } = organization;
    const phoneNumbers = organization.phoneNumbers || [];
    const otherNumbers =
      phoneNumbers.filter(phone => phone.type.toLowerCase() !== 'business') ||
      [];
    const sentPhoneNumbers = [...otherNumbers, businessNumber]
      .map(phone => {
        return {
          ...phone,
          number: scrubNumber(phone.number),
        };
      })
      .filter(phone => phone.number.length);

    const requestBody = {
      ...rest,
      addresses: sentAddresses,
      phoneNumbers: sentPhoneNumbers,
    };

    return this.props.actions.organization
      .put(requestBody)
      .then(({ payload }) => {
        const { resp, json } = payload;

        if (resp.ok) {
          // show success message
          this.setState(prevState => ({
            ...prevState,
            organization: this.loadOrgFromProps({ currentOrganization: json }),
            submitSuccess: true,
          }));

          // update form info with latest org data
          return onCompleteHandler(false, json);
        } else if (resp.status === 500) {
          return this.context.activateServiceDialog();
        } else {
          // get any error messages
          this.setState(prevState => ({
            ...prevState,
            errors: {
              ...prevState.errors,
              form: json && json.message ? [json.message] : [],
            },
          }));

          // parse detailed errors if they exists
          let errors =
            json && json.detailedErrors
              ? json.detailedErrors.map(error => ({
                  ...error,
                  propertyName: `organization.${normalizePropertyPath(
                    error.propertyName
                  )}`,
                }))
              : [];

          // parse phone and address errors
          if (errors.length) {
            errors = errors.map(error => {
              if (
                error.propertyName
                  .toLowerCase()
                  .includes('phoneNumbers'.toLowerCase())
              ) {
                return {
                  ...error,
                  propertyName: 'organization.businessNumber.number',
                };
              }
              if (error.propertyName.toLowerCase().includes('addresses')) {
                const errorIndex = error.propertyName.match(/\[(.*?)\]/)[1];
                const addressWithError = sentAddresses[errorIndex];
                const addressIndex = form.organization.addresses.findIndex(
                  address => address.type === addressWithError.type
                );
                return {
                  ...error,
                  propertyName: error.propertyName.replace(
                    /\[(.*?)\]/,
                    `[${addressIndex}]`
                  ),
                };
              }
              return error;
            });
          }

          return onCompleteHandler(json.message, errors);
        }
      });
  };

  render() {
    const { organization, submitSuccess, errors } = this.state;
    const errorsObj = {
      success: submitSuccess,
      errors: submitSuccess ? ['Changes were saved successfully'] : errors.form,
    };
    let addresses = this.getAddresses();

    // if any addresses are marked to be same as business
    const sameAsBusinessList = this.getSameAsBusinessList();
    if (sameAsBusinessList.length) {
      addresses = addresses.map(address => {
        const addressType = address.type;
        const businessAddressIndex = addresses.findIndex(
          address => address.type.toLowerCase() === 'business'
        );
        if (sameAsBusinessList.some(item => item === addressType)) {
          // override path on each type that is marked as same
          return {
            ...address,
            pathOverride: `organization.addresses[${businessAddressIndex}]`,
          };
        }
        return address;
      });
    }

    return (
      <CardText>
        <Form
          initState={{ organization }}
          formInputs={this.getFormInputs(addresses)}
          formSubmit={this.handleSubmit}
          formErrors={errorsObj}
          warnOnExit
          preventSubmit
          listenToState
        />
      </CardText>
    );
  }
}



// WEBPACK FOOTER //
// ./src/App/account/components/MembershipInfo/BusinessEdit/index.js